import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import ScrollingContent from './ScrollingContent';
import { truthyOrZero } from '@/helpers/math';

// Get the width of the element held in a ref
const getWidthFromRef = (ref) => {
  return ref.current
    ? ref.current.offsetWidth
    : 0;
};

const getPaddingLeftFromRef = (ref) => {
  return ref.current
    ? Number(window.getComputedStyle(ref.current).getPropertyValue('padding-left').replace('px', '')) || 0
    : 0;
};

// Calculate the ratio (of widths) between the fixed wrapper (overflow: hidden)
// to the scrolling child content
const calculateWrapperToChildWidthRatio = (wrapperWidth, contentWidth) => {
  return (contentWidth === 0)
    ? 0
    : (wrapperWidth / contentWidth);
};

const AutoScroller = ({
  children,
  style,
  contentStyle,
  contentProps,
  autoscroll,
  forceScroll,
  onCompleteScroll,
  onCalculateWidth,
  scrollDuration, // Transition time in seconds
  infiniteScroll, // Scroll will loop infinitely if true
  matchSpeedParams, // { duration, width } - A duration/width pair to match speed with another scroller
  startingPercentage, // Starting percentage (eg 50) relative to the fixed wrapper width
  startingDelay, // Delay in ms before scrolling starts
  pauseDelay, // Delay in ms between scrolls
  percentageGap, // Percentage (eg 50) gap between the two scrolling elements
  centered
}) => {
  // Use a state updater to force re-render as some changes
  // are tracked through refs. Ref changes don't cause updates
  const [, forceUpdate] = useState({});
  // Tracks whether the content is scrolling
  const [scrolling, setScrolling] = useState(false);
  // Tracks whether the content has already autoscrolled
  const [hasAutoscrolled, setHasAutoscrolled] = useState(false);
  // Ref to track the fixed width wrapping div
  const wrapperRef = useRef(null);
  // Ref to track the scrolling content
  const contentRef = useRef(null);
  // Timer for programmatic control over scrolling
  const timer = useRef(null);

  // ---- COMPUTE VALUES ----
  const wrapperWidthPx = getWidthFromRef(wrapperRef);
  const contentWidthPx = getWidthFromRef(contentRef);
  const widthsAreComputed = truthyOrZero(contentWidthPx) && truthyOrZero(contentWidthPx);

  const contentNeedsScrolling = widthsAreComputed && !!contentWidthPx && !!((contentWidthPx + Number(getPaddingLeftFromRef(contentRef))) > wrapperWidthPx);
  const wrapperToChildWidthRatio = calculateWrapperToChildWidthRatio(wrapperWidthPx, contentWidthPx);
  // Proportionally calculate the starting percentage for child content
  const startingPercentageForContent = wrapperToChildWidthRatio * startingPercentage;
  // Ref to track the current horizontal transform percentage of the scrolling content
  const xTransformRef = useRef(startingPercentageForContent);
  // Content is ready for loop once it has scrolled 100% plus the percentage gap
  const loopPercentageForContent = -(100 + (wrapperToChildWidthRatio * percentageGap));

  // ---- SCROLL FUNCTIONS ----
  const updateXTransformPerc = (perc, operation = null) => {
    if (operation === 'add') {
      xTransformRef.current += perc;
    } else {
      xTransformRef.current = perc;
    }
  };

  const clearScrollTimer = useCallback(() => clearTimeout(timer.current), []);

  const startScroll = (delay = 0) => {
    clearScrollTimer();
    
    if (!delay) {
      setScrolling(true);
    } else {
      timer.current = setTimeout(() => {
        setScrolling(true);
      }, delay);
    }
  };

  const stopScroll = () => {
    setScrolling(false);
    clearScrollTimer();
  };

  const loopScroll = () => {
    updateXTransformPerc(0);
    stopScroll();
  };

  const handleTransitionEnd = useCallback(() => {
    if (!hasAutoscrolled) {
      setHasAutoscrolled(true);
    }

    loopScroll();

    if (onCompleteScroll) {
      onCompleteScroll();
    }

    if (infiniteScroll) {
      startScroll(pauseDelay);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- INITIALLY SETUP SCROLLING AND START POSITION ----
  useEffect(() => {
    if (widthsAreComputed) {
      if (onCalculateWidth) {
        onCalculateWidth(contentWidthPx, contentNeedsScrolling);
      }

      if (contentNeedsScrolling) {
        if (Math.abs(xTransformRef.current - startingPercentageForContent)) {
          // Set the starting position if the widths are known and there is a difference
          updateXTransformPerc(startingPercentageForContent);
          forceUpdate({});
        }

        if (autoscroll && !hasAutoscrolled) {
          startScroll(startingDelay);
        }
      } else {
        loopScroll();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthsAreComputed, contentNeedsScrolling, autoscroll, contentWidthPx]);

  // ---- HANDLE ONGOING SCROLLING ----
  useEffect(() => {
    const content = contentRef.current;

    content.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      content.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [handleTransitionEnd, scrolling]);

  // ---- HANDLE MANUAL SCROLLING ----
  useEffect(() => {
    if (forceScroll && contentNeedsScrolling) {
      startScroll();
    } else {
      stopScroll();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceScroll]);

  useEffect(() => () => clearScrollTimer(), [clearScrollTimer]);

  const transitionDuration = matchSpeedParams
    ? ((contentWidthPx / matchSpeedParams.width) * matchSpeedParams.duration)
    : scrollDuration;

  return (
    <div style={ {
      ...style,
      position: 'relative'
    } }
    >
      <ScrollingContent
        centered={ centered && !contentNeedsScrolling }
        contentRef={ contentRef }
        contentStyle={ {
          ...contentStyle,
          transition: scrolling ? `all ${ transitionDuration }s linear` : 'unset',
          transform: `translateX(${ scrolling ? loopPercentageForContent : xTransformRef.current }%)`,
          zIndex: 1
        } }
        wrapperRef={ wrapperRef }
        { ...contentProps }
      >
        { children }
      </ScrollingContent>
      { contentNeedsScrolling && (
        <ScrollingContent
          contentStyle={ {
            ...contentStyle,
            transition: scrolling ? `all ${ transitionDuration }s linear` : 'unset',
            transform: `translateX(${ scrolling ? loopPercentageForContent + 100 : xTransformRef.current + 100 }%)`,
            marginLeft: `${ percentageGap }%`
          } }
          { ...contentProps }
        >
          { children }
        </ScrollingContent>
      ) }
    </div>
  );
};

AutoScroller.propTypes = {
  style: PropTypes.object,
  onCalculateWidth: PropTypes.func,
  autoscroll: PropTypes.bool,
  forceScroll: PropTypes.bool,
  onCompleteScroll: PropTypes.func,
  scrollDuration: PropTypes.number,
  infiniteScroll: PropTypes.bool,
  matchSpeedParams: PropTypes.object,
  startingPercentage: PropTypes.number,
  startingDelay: PropTypes.number,
  pauseDelay: PropTypes.number,
  contentStyle: PropTypes.object,
  percentageGap: PropTypes.number,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  contentProps: PropTypes.object,
  centered: PropTypes.bool
};

AutoScroller.defaultProps = {
  style: {},
  onCalculateWidth: undefined,
  autoscroll: false,
  forceScroll: false,
  onCompleteScroll: undefined,
  scrollDuration: 5,
  infiniteScroll: false,
  matchSpeedParams: null,
  startingPercentage: 0,
  startingDelay: 5000,
  contentStyle: {},
  percentageGap: 40,
  pauseDelay: 3000,
  contentProps: {},
  centered: false
};

export default AutoScroller;